import type { GisLens, GisOverview, MandalAggregate } from '../api/types'

/**
 * Lens hero/tile figures, read straight from the server-precomputed `summary` rollup (the per-mandal
 * rows are summed server-side, excluding unmapped sites). `null` aggregates render as 0 in bars.
 */
export const statusWord = (v: number): string =>
  v >= 85 ? 'Strong' : v >= 60 ? 'Moderate' : 'Needs attention'

export interface HeroBar {
  label: string
  pct: number // 0–100 bar width
  text: string // value rendered to the right
}

export interface HeroData {
  kicker: string
  pct: number // donut value
  status: string
  target: string
  bars: HeroBar[]
}

export function heroDataFor(lens: GisLens, ov: GisOverview): HeroData {
  const s = ov.summary
  if (lens === 'coverage') {
    const coverage = s.coverage ?? 0
    const geocoded = s.geocoded ?? 0
    return {
      kicker: 'SURVEY COVERAGE', pct: coverage, status: statusWord(coverage), target: 'target 85%',
      bars: [
        { label: 'Coverage', pct: coverage, text: `${coverage}%` },
        { label: 'Geocoded', pct: geocoded, text: `${geocoded}%` },
        { label: 'Pending', pct: 100 - coverage, text: `${100 - coverage}%` },
      ],
    }
  }
  if (lens === 'issues') {
    const share = s.criticalSharePct ?? 0
    const avgRes = s.avgResolutionDays ?? 0
    return {
      kicker: 'CRITICAL SHARE', pct: share, status: share < 20 ? 'Contained' : 'Elevated', target: 'target <20%',
      bars: [
        { label: 'Active', pct: 100, text: `${s.activeIssues}` },
        { label: 'Critical', pct: share, text: `${s.criticalIssues}` },
        { label: 'Avg resolution', pct: avgRes * 4, text: `${avgRes}d` }, // may exceed 100; HeroCard clamps at render
      ],
    }
  }
  const off = s.officerAvgCompletion ?? 0
  return {
    kicker: 'OFFICER PERFORMANCE', pct: off, status: statusWord(off), target: 'target 85%',
    bars: [
      { label: 'Avg completion', pct: off, text: `${off}%` },
      { label: 'Officers', pct: 100, text: `${s.officerCount}` },
      { label: 'Mandals', pct: 100, text: `${s.mandalCount}` },
    ],
  }
}

export type KpiTile = readonly [string | number, string]

export function tilesFor(lens: GisLens, ov: GisOverview): KpiTile[] {
  const s = ov.summary
  if (lens === 'coverage') {
    return [
      [`${s.geocoded ?? 0}%`, 'Geocoded'],
      [ov.mandals.filter((m) => m.coverage !== null && m.coverage >= 85).length, 'Mandals ≥85%'],
      [ov.mandals.reduce((a, m) => a + m.surveyedSites, 0), 'Surveyed sites'],
      [ov.mandals.reduce((a, m) => a + m.neverSurveyedSites, 0), 'Never surveyed'],
    ]
  }
  if (lens === 'issues') {
    return [
      [s.activeIssues, 'Active issues'],
      [s.criticalIssues, 'Critical'],
      [`${s.avgResolutionDays ?? 0}d`, 'Avg resolution'],
      [ov.mandals.filter((m) => m.activeIssues > 25).length, 'Hotspot mandals'],
    ]
  }
  return [
    [s.officerCount, 'Officers'],
    [`${s.officerAvgCompletion ?? 0}%`, 'Avg completion'],
    [s.topPerformer?.name ?? '—', 'Top performer'],
    [s.officersBelow60Count, 'Below 60%'],
  ]
}

/** Officer KPI tiles scoped to one mandal row (parity with `tilesFor('officers', …)`). */
export function officerTilesForMandal(m: MandalAggregate): KpiTile[] {
  return [
    [m.officerCount, 'Officers'],
    [`${m.officerAvgCompletion ?? 0}%`, 'Avg completion'],
    [m.topPerformer?.name ?? '—', 'Top performer'],
    [m.officersBelow60Count, 'Below 60%'],
  ]
}
